/**
 * Persistence for the aggregate header's brand filter.
 *
 * Keyed by subtree root so an operator who delegates into two different aggregators
 * keeps a selection per aggregator. "Absent" means "all brands" — deliberately not
 * stored as the full id list, so a brand added to the subtree afterwards is included
 * rather than silently filtered out.
 */

const KEY_PREFIX = 'aggregate:selectedBrandIds:';

function storageKey(rootMasterClientId: number): string {
  return `${KEY_PREFIX}${rootMasterClientId}`;
}

/**
 * The stored selection intersected with the brands the session can actually see, or
 * `null` for "all". An intersection that comes back empty (every stored brand left the
 * subtree) also reads as "all" rather than as a filter that hides everything.
 */
export function loadSelectedBrandIds(rootMasterClientId: number, availableBrandIds: string[]): string[] | null {
  let raw: string | null = null;
  try {
    raw = localStorage.getItem(storageKey(rootMasterClientId));
  } catch {
    return null;
  }
  if (!raw) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!Array.isArray(parsed)) return null;
  const available = new Set(availableBrandIds);
  const intersected = parsed.filter((id): id is string => typeof id === 'string' && available.has(id));
  return intersected.length > 0 ? intersected : null;
}

/** Persist the selection; `null` (or "everything is selected") clears the key. */
export function saveSelectedBrandIds(
  rootMasterClientId: number,
  selected: string[] | null,
  availableBrandIds: string[],
): void {
  try {
    if (!selected || selected.length === 0 || selected.length === availableBrandIds.length) {
      localStorage.removeItem(storageKey(rootMasterClientId));
      return;
    }
    localStorage.setItem(storageKey(rootMasterClientId), JSON.stringify(selected));
  } catch {
    // Private mode / storage full — the filter still works for this tab.
  }
}
